import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { FlagIcon } from '@heroicons/react/24/outline'
import { WeekOfTheYearKey } from 'classes/WeekOfTheYearKey'

import KinesisProgramWeekKeyIcon from 'components/Athlete/v2/KinesisProgramWeeks/KinesisProgramWeekKeyIcon'
import KinesisProgramWeekCompletionStatusIcon from 'components/Athlete/v2/KinesisProgramWeeks/KinesisProgramWeekCompletionStatusIcon'

export default function KinesisProgramWeekBar({
    displaySize = 'small',
    isCurrentlySelectedWeek = false,
    isPresentWeek = false,
    trainingChartWeekIndex,
    trainingChartWeekData,
    programWeekBarStyle,
    eventsInWeek = [],
    programWeekKey,
    weekCompletionStatusIconKey,
    OnSetWeekButton = () => {}
}) {
    const containerRef = useRef(null)
    const [showHoverInfo, setShowHoverInfo] = useState(false)
    const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 })

    const GetTrainingChartWeekProgramPhase = (trainingChartWeekData) => {

        var programPhaseKey = 'none'
        
        switch (trainingChartWeekData.trainingChartDataKey) {
            case 'empty': programPhaseKey = 'none'; break;
            case 'past': programPhaseKey = trainingChartWeekData.overviewFile?.programPhaseKey; break;
            case 'active': programPhaseKey = trainingChartWeekData.overviewFile?.programPhaseKey; break;
            case 'future': programPhaseKey = trainingChartWeekData.programTableColumn?.volumeData?.programPhaseKey; break;
            default: programPhaseKey = 'none'; break;
        }

        if (programPhaseKey === 'none') return programPhaseKey

        // Format key into readable text
        var programPhaseName = programPhaseKey
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

        return programPhaseName
    }

    useEffect(() => {
        if (showHoverInfo && containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect()
            setPopupPosition({
                top: rect.top - 10,
                left: rect.left + rect.width / 2
            })
        }
    }, [showHoverInfo])

    return (
        <div 
            ref={containerRef}
            className={`
                relative
                ${isPresentWeek ? 'outline outline-[2px] outline-orange-300 outline-offset-[-1px] rounded-lg pt-2 sm:pt-3 pb-0' : ''}
            `}
            onMouseEnter={() => setShowHoverInfo(true)}
            onMouseLeave={() => setShowHoverInfo(false)}
            onTouchStart={() => setShowHoverInfo(true)}
            onTouchEnd={() => setShowHoverInfo(false)}
        >
            <div 
                className={`
                    ${displaySize === 'small' ? 'h-[95px] sm:h-[165px] mx-[0.5px] sm:mx-[1px] px-[0.5px] sm:px-[1px]' : ''}
                    ${displaySize === 'large' ? 'h-[340px] mx-[2px] px-[2px]' : ''}
                    ${isPresentWeek ? '-mt-2 sm:-mt-3' : ''}
                    flex flex-col items-center justify-end relative
                    pb-1 sm:pb-2 space-y-1 sm:space-y-2 rounded-md
                    cursor-pointer hover:opacity-80
                `}
                onClick={() => OnSetWeekButton(trainingChartWeekIndex)}
            >
                {isCurrentlySelectedWeek === true &&
                    <div className='mb-auto'>
                        <ChevronDownIcon 
                            className={`
                                ${displaySize === 'small' ? 'h-4 sm:h-5' : ''}
                                ${displaySize === 'large' ? 'h-10 w-10' : ''}
                                -mt-[1px] sm:-mt-[2px] -mb-1 sm:-mb-2 text-gray-400 pointer-events-none
                            `}
                        />
                    </div>
                }

                <div className={programWeekBarStyle}>
                    {GetTrainingChartWeekProgramPhase(trainingChartWeekData) !== 'none' &&
                        <div
                            className={`
                                absolute bottom-0 left-0 w-full 
                                ${false ? 'bg-gradient-to-b from-orange-400/80 to-orange-500/70' : 'bg-orange-400'}
                                ${displaySize === 'small' ? 'rounded-md' : ''}
                                ${displaySize === 'large' ? 'rounded-xl' : ''}
                            `}
                            style={{ height: displaySize === 'large' ? 'calc(100% - 18px)' : 'calc(100% - 7px)' }}
                        />
                    }
                </div>

                {eventsInWeek.length > 0 &&
                    <div 
                        className={`
                            absolute left-0 right-0 flex flex-col items-center text-center
                            ${displaySize === 'small' ? 'top-[40px] sm:top-[72px]' : ''}
                            ${displaySize === 'large' ? 'top-[192px]' : ''}
                        `}
                    >
                        <FlagIcon 
                            className={`
                                text-white
                                ${displaySize === 'small' ? 'h-3 sm:h-4' : ''}
                                ${displaySize === 'large' ? 'h-8 w-8' : ''}
                            `}
                        />
                    </div>
                }

                <KinesisProgramWeekKeyIcon displaySize={displaySize} programWeekKey={programWeekKey} />

                <KinesisProgramWeekCompletionStatusIcon displaySize={displaySize} weekCompletionStatusIconKey={weekCompletionStatusIconKey} />
            </div>

            {showHoverInfo && createPortal(
                <div 
                    className={`
                        fixed z-50 bg-white border border-gray-400 rounded-lg shadow-lg min-w-[200px]
                        p-4 text-center space-y-2 pointer-events-none
                    `}
                    style={{
                        top: `${popupPosition.top}px`,
                        left: `${popupPosition.left}px`,
                        transform: 'translate(-50%, -100%)',
                        marginBottom: '8px'
                    }}
                >
                    <div>
                        <div className='text-xxs text-gray-600'>{trainingChartWeekData.weekOfTheYearKey}</div>
                        <div className='text-sm'>{new WeekOfTheYearKey(trainingChartWeekData.weekOfTheYearKey).toDateRangeString('MMMM D, YYYY')}</div>
                    </div>

                    {GetTrainingChartWeekProgramPhase(trainingChartWeekData) !== 'none' &&
                        <div className='text-sm'>Program Phase: {GetTrainingChartWeekProgramPhase(trainingChartWeekData)}</div>
                    }
                </div>,
                document.body
            )}
        </div>
    )
}